import { useState } from "react";
import { Link } from "react-router-dom";

import { useMarketItems, useScanOffers } from "../api/queries";
import type { MarketItem, MarketQuote, OfferMode } from "../api/types";
import "./pages.css";
import { Empty } from "../components/Empty";
import { ErrorBand } from "../components/ErrorBand";
import { Panel } from "../components/Panel";
import { Skeleton } from "../components/Skeleton";
import { TitleBand } from "../components/TitleBand";
import { WatchStar } from "../components/WatchStar";
import { Variation, VariantPrices } from "../components/Variation";
import { adena, ago, day, plural, signed, when } from "../format";
import { itemHref, traderHref } from "../search/nav";
import type { ItemSort } from "./marketSort";
import {
  crossScanSpread, delta, filterByItem, isStale, sortMarketItems, spread, whereCity,
} from "./marketSort";

const SORTS: ItemSort[] = ["seen", "item", "buy", "sell", "spread"];
const SORT_LABELS: Record<ItemSort, string> = {
  seen: "last seen", item: "item", buy: "best buy", sell: "best sell", spread: "spread",
};

/** One item's offers from the one scan its number came from — so the traders
 *  listed add up to the `best` and `#` in the row that opened this. It reuses
 *  /scans/{uid}/offers unchanged; carrying scan_uid on every quote is what makes
 *  that possible without a new endpoint.
 *
 *  One panel per side that exists. When both sides came from the same scan,
 *  react-query dedupes the two to a single request: same uid, same item. */
function SidePanel({ itemId, item, mode, quote }: {
  itemId: number; item: string; mode: OfferMode; quote: MarketQuote;
}) {
  const offers = useScanOffers(quote.scan_uid, item);
  const rows = (offers.data?.offers ?? []).filter((row) => row.mode === mode);
  // The count is of the rows actually rendered, not the scan's own total for
  // this mode. They differ when the 200-row fetch — which spans every mode —
  // truncated before this mode's rows were exhausted. Quoting quote.offers
  // alone would claim more rows than sit underneath it.
  //
  // `rows.length` is 0 while the fetch is in flight and 0 when it failed, so the
  // "showing N of M" form is only true once the data is actually here. Until
  // then the scan's own count is the honest thing to print: it comes from the
  // aggregate, not from this request, so it is already known and already correct.
  const shown = offers.isSuccess && rows.length !== quote.offers
    ? `showing ${rows.length} of ${quote.offers} in that scan`
    : `${quote.offers} in that scan`;
  // The words MarketOffers.tsx's LABELS already puts over these two columns. A
  // craft row is a crafter charging a fee, not a trader quoting a price — and
  // both panels open for the same item, so one column called two things reads
  // as two different columns.
  const who = mode === "craft" ? "Crafter" : "Trader";
  const priceLabel = mode === "craft" ? "Fee" : "Price";

  return (
    <>
      <TitleBand
        title={`${item} — ${mode}`}
        note={`${quote.city || "unknown town"}, ${ago(quote.observed_at)} · ${shown}`}
        action={<WatchStar itemId={itemId} item={item} mode={mode} />}
      />
      <Panel>
        {offers.isLoading ? (
          <Skeleton rows={3} />
        ) : offers.isError ? (
          // Without this the table renders its headers over zero rows, which
          // reads as "this item had no offers" — a state that cannot occur,
          // since the row above was derived from at least one.
          <ErrorBand
            message="Could not load that scan's offers."
            hint="The market itself loaded fine. Reload in a moment."
          />
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>{who}</th>
                  {/* A craft row is a fee for a service, and a fee has no
                      quantity — the column is not empty there, it is
                      meaningless. Offers.tsx and MarketOffers.tsx drop it for
                      the same reason. */}
                  {mode === "craft" ? null : <th className="num">Qty</th>}
                  <th className="num">{priceLabel}</th>
                  <th>Via</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <Link to={traderHref(row.nick)}>{row.nick}</Link>
                      {/* The panel is already one item; this says which of it
                          they had. */}
                      <Variation {...row} />
                    </td>
                    {mode === "craft" ? null : <td className="num">{adena(row.qty)}</td>}
                    <td className={`num ${mode === "craft" ? "" : mode}`}>{adena(row.price)}</td>
                    <td className="dim">{row.via_item}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}

/** The index: every item ever scanned, with what is currently known about each.
 *
 *  Not one scan. `/scans/:uid` is the page for that, and it keeps its own meaning
 *  and its own facts — city, source, duration — which are facts about one walk
 *  and would repeat down every row here. */
export function Market() {
  const [sort, setSort] = useState<ItemSort>("seen");
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState<number | null>(null);

  const market = useMarketItems();

  if (market.isError) {
    return (
      <>
        <TitleBand title="Items" />
        <ErrorBand
          message="Could not load the market."
          hint="The server may be restarting. Reload in a moment."
        />
      </>
    );
  }
  // Undefined while in flight, and also while react-query has the query paused
  // because the browser is offline — a paused query has isLoading false and no
  // data, and destructuring that is what used to throw here.
  if (!market.data) {
    return <><TitleBand title="Items" /><Skeleton rows={8} /></>;
  }

  const { items, scans, oldest, truncated } = market.data;
  const rows = sortMarketItems(filterByItem(items, filter), sort);
  const needle = filter.trim();
  // From the filtered rows, not from items: filtering to something else hides
  // the panel, and clearing the filter brings it back, because `selected` still
  // holds the id. Intended — a panel stranded under a table that no longer
  // shows its row is worse than either.
  const chosen = rows.find((row) => row.item_id === selected);

  // `scans`, not `items.length`. An empty `items` means no item has a priced
  // sighting, not that nothing was ever scanned: a scan that walked packages
  // only leaves `items` empty while the history holds a walk, and telling that
  // operator to go run a scan sends them after a problem they do not have.
  if (scans === 0) {
    return (
      <>
        <TitleBand title="Items" />
        <Panel primary>
          <Empty>
            No scans yet. Run <code>python -m market_analyzer scan</code> on the
            scanning machine, then <code>sync</code>.
          </Empty>
        </Panel>
      </>
    );
  }

  return (
    <>
      <TitleBand
        title="Items"
        note={`${plural(items.length, "item")} · ${plural(scans, "scan")}`}
      />
      <p className="scan-meta">
        {/* `items[0]` only exists once some item has a priced sighting, and
            scans can exist without that — a walk of packages alone. The page
            that has to explain that state cannot be the one that throws
            reaching into an empty list for it. */}
        {items.length > 0 ? <span>newest {ago(items[0].last_seen)}</span> : null}
        {items.length > 0 && oldest !== null
          ? <span aria-hidden="true">·</span> : null}
        {oldest !== null ? <span>oldest {day(oldest)}</span> : null}
      </p>
      <Panel primary>
        <div className="strip">
          <span>Sort</span>
          {SORTS.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={sort === option}
              onClick={() => setSort(option)}
            >
              {SORT_LABELS[option]}
            </button>
          ))}
          <span style={{ flex: 1 }} />
          <input
            aria-label="Filter items"
            placeholder="filter item…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>

        {truncated ? (
          <p className="scan-meta">
            showing the {items.length.toLocaleString()} most recently seen items —
            the history holds more
          </p>
        ) : null}

        {rows.length === 0 ? (
          needle ? (
            <Empty>Nothing matching “{needle}”.</Empty>
          ) : (
            // Scans exist — the early return above proved it — but none of them
            // left a priced sighting behind. Saying "nothing matching ''" here
            // would blame a filter nobody typed.
            <Empty>
              No item has a priced sighting yet. The scans so far walked
              packages only, or found nothing on offer.
            </Empty>
          )
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="num">Best buy</th>
                  <th className="num">#</th>
                  <th className="num">Best sell</th>
                  <th className="num">#</th>
                  <th className="num">Spread</th>
                  <th className="num">Buy vs last</th>
                  <th className="num">Sell vs last</th>
                  <th>Where</th>
                  <th className="num">Last seen</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <ItemRow
                    key={row.item_id}
                    row={row}
                    selected={row.item_id === selected}
                    onSelect={() =>
                      setSelected(row.item_id === selected ? null : row.item_id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {chosen?.buy ? (
        <SidePanel itemId={chosen.item_id} item={chosen.item} mode="buy" quote={chosen.buy} />
      ) : null}
      {chosen?.sell ? (
        <SidePanel itemId={chosen.item_id} item={chosen.item} mode="sell" quote={chosen.sell} />
      ) : null}
      {/* Craft too, or an item whose only sighting is a crafter's fee is a dead
          row: it shows a Where and a Last seen drawn from that quote, sorts
          among the priced rows, and opens nothing when clicked. */}
      {chosen?.craft ? (
        <SidePanel itemId={chosen.item_id} item={chosen.item} mode="craft" quote={chosen.craft} />
      ) : null}
    </>
  );
}

function ItemRow({ row, selected, onSelect }: {
  row: MarketItem; selected: boolean; onSelect: () => void;
}) {
  const where = whereCity(row);
  const apart = crossScanSpread(row);

  return (
    <tr className={selected ? "sel" : undefined} onClick={onSelect}>
      <td>
        <Link to={itemHref(row.item_id, row.item)} onClick={(e) => e.stopPropagation()}>
          {row.item}
        </Link>
        {/* This item has never been listed plain, so all three of its quotes
            are its variants'. The row sorts and compares against plain rows in
            every column beside it, which is exactly why it has to say so. */}
        <VariantPrices from={row.from_variants} />
      </td>
      <td className="num buy">{adena(row.buy?.best ?? null)}</td>
      <td className="num dim">{row.buy?.traders || ""}</td>
      <td className="num sell">{adena(row.sell?.best ?? null)}</td>
      <td className="num dim">{row.sell?.traders || ""}</td>
      {/* Dimmed when the two sides came from different scans: a spread between a
          two-hour-old bid and a three-week-old ask is arithmetic, not a market. */}
      <td
        className={`num ${apart ? "dim" : ""}`}
        title={apart
          ? "The buy and the sell were seen in different scans, so this is not a live spread."
          : undefined}
      >
        {signed(spread(row))}
      </td>
      <td
        className={`num ${delta(row.buy) === null ? "dim" : ""}`}
        title="Against the previous scan that saw this item"
      >
        {signed(delta(row.buy))}
      </td>
      <td
        className={`num ${delta(row.sell) === null ? "dim" : ""}`}
        title="Against the previous scan that saw this item"
      >
        {signed(delta(row.sell))}
      </td>
      <td className="dim" title={where.title}>{where.city}</td>
      <td
        className={`num ${isStale(row.last_seen) ? "dim" : ""}`}
        title={when(row.last_seen)}
      >
        {ago(row.last_seen)}
      </td>
    </tr>
  );
}
